import {
  TbPointer,
  TbArrowsMove,
  TbRotate,
  TbArrowsMaximize,
  TbWorld,
  TbEye,
  TbView360,
  TbFocus2,
  TbGrid4X4,
  TbCube,
  TbGradienter,
  TbFrame,
  TbSparkles,
} from "react-icons/tb";

const GIZMO_MODES = [
  { mode: "select", label: "Select (1)", Icon: TbPointer },
  { mode: "translate", label: "Move (2)", Icon: TbArrowsMove },
  { mode: "rotate", label: "Rotate (3)", Icon: TbRotate },
  { mode: "scale", label: "Scale (4)", Icon: TbArrowsMaximize },
];

const CAMERA_MODES = [
  {
    mode: "look",
    label: "Look around — drag to rotate the camera in place",
    Icon: TbEye,
  },
  {
    mode: "orbit",
    label: "Orbit — drag to rotate the camera around its origin",
    Icon: TbView360,
  },
];

// More passes (normal, AO, wireframe, ...) can grow this list later — each
// just needs a way to render into the viewport's color buffer (see the
// `renderMode` effect in Viewport.jsx) — "aiObjects" is the odd one out,
// an overlay drawn on top of Shaded rather than a material swap (see the
// dedicated overlay canvas in Viewport.jsx instead).
const RENDER_PASSES = [
  { id: "shaded", label: "Shaded", Icon: TbCube },
  { id: "depth", label: "Depth Map", Icon: TbGradienter },
  { id: "aiObjects", label: "AI Objects", Icon: TbFrame },
];

export default function ManipulationPanel({
  gizmoMode,
  onGizmoModeChange,
  gizmoSpace,
  onGizmoSpaceChange,
  cameraMode,
  onCameraModeChange,
  onFocusCamera,
  canFocusCamera,
  gridVisible,
  onToggleGrid,
  renderMode,
  onRenderModeChange,
  aiViewMode,
  onToggleAiViewMode,
  hasAiRenders,
}) {
  const inactiveBtn =
    "flex items-center justify-center w-9 h-9 rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 cursor-pointer";
  const activeBtn =
    "flex items-center justify-center w-9 h-9 rounded-md bg-primary text-primary-foreground cursor-pointer";
  const disabledBtn =
    "flex items-center justify-center w-9 h-9 rounded-md bg-secondary text-secondary-foreground/30 cursor-not-allowed";

  return (
    <div
      id="Scene3DManipulationPanel"
      className="flex flex-col items-center gap-1 p-2 h-full">
      <span className="text-[8px] text-secondary-foreground/60">
        Manipulate
      </span>
      {GIZMO_MODES.map(({ mode, label, Icon }) => (
        <button
          key={mode}
          id={`Scene3DManipulationPanel__mode-${mode}`}
          onClick={() => onGizmoModeChange(mode)}
          className={gizmoMode === mode ? activeBtn : inactiveBtn}
          title={label}>
          <Icon className="w-4 h-4" />
        </button>
      ))}

      <span className="text-[8px] text-secondary-foreground/60 mt-2">Axis</span>
      <button
        id="Scene3DManipulationPanel__spaceToggle"
        onClick={() =>
          onGizmoSpaceChange(gizmoSpace === "world" ? "local" : "world")
        }
        className={inactiveBtn + " flex-col gap-0.5"}
        title="Toggle gizmo space — world vs. object-local (scale is always local, a three.js limitation)">
        <TbWorld className="w-4 h-4" />
        <span className="text-[8px] capitalize leading-none">{gizmoSpace}</span>
      </button>

      <span className="text-[8px] text-secondary-foreground/60 mt-2">
        Camera
      </span>
      {CAMERA_MODES.map(({ mode, label, Icon }) => (
        <button
          key={mode}
          id={`Scene3DManipulationPanel__cameraMode-${mode}`}
          onClick={() => onCameraModeChange(mode)}
          className={cameraMode === mode ? activeBtn : inactiveBtn}
          title={label}>
          <Icon className="w-4 h-4" />
        </button>
      ))}

      <button
        id="Scene3DManipulationPanel__focusCamera"
        onClick={onFocusCamera}
        disabled={!canFocusCamera}
        className={canFocusCamera ? inactiveBtn : disabledBtn}
        title={
          canFocusCamera
            ? "Focus camera (F) — moves the camera's origin onto the selected object"
            : "Select an object to focus the camera on"
        }>
        <TbFocus2 className="w-4 h-4" />
      </button>

      <span className="text-[8px] text-secondary-foreground/60 mt-2">View</span>
      {RENDER_PASSES.map(({ id, label, Icon }) => (
        <button
          key={id}
          id={`Scene3DManipulationPanel__renderPass-${id}`}
          onClick={() => onRenderModeChange(id)}
          // Viewing the AI render rounds off whichever pass was active —
          // `renderMode` itself is untouched, so toggling back to Scene
          // just re-shows it, no separate "last active pass" to track.
          className={
            renderMode === id && aiViewMode !== "render"
              ? activeBtn
              : inactiveBtn
          }
          title={label}>
          <Icon className="w-4 h-4" />
        </button>
      ))}

      {hasAiRenders && (
        <button
          id="Scene3DManipulationPanel__aiViewToggle"
          onClick={onToggleAiViewMode}
          className={aiViewMode === "render" ? activeBtn : inactiveBtn}
          title={aiViewMode === "render" ? "View 3D Scene" : "View AI Render"}>
          <TbSparkles className="w-4 h-4" />
        </button>
      )}

      <span className="text-[8px] text-secondary-foreground/60 mt-2">Grid</span>

      <button
        id="Scene3DManipulationPanel__gridToggle"
        onClick={() => onToggleGrid(!gridVisible)}
        className={gridVisible ? activeBtn : inactiveBtn}
        title={gridVisible ? "Hide grid (G)" : "Show grid (G)"}>
        <TbGrid4X4 className="w-4 h-4" />
      </button>
    </div>
  );
}
